import React, { useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import InfluencerIdentityRow, {
  InfluencerIdentityRowRoot,
} from '@components/InfluencerIdentity/Row'
import { EventTrackingNames, SubmissionChannels } from '@enums'
import { IApplicationState } from '@store'
import {
  fetchSubmissions,
  selectIdentityAge,
  selectIdentityAvatar,
  selectIdentityFormattedLocationAndGender,
  selectIdentityFullName,
  selectIdentityHasGroup,
  selectIdentityProvider,
  selectIdentitySocialPageUrl,
  selectIdentitySubmissionsLink,
  selectIdentityUsername,
  selectIsBrandFanIdentityOpened,
  toggleIdentity,
} from '@store/brandFans'
import InfluencerIdentityRowSubmissionWrapper from './Submissions/Wrapper'
import BrandFanInfluencerIdentityRowActions from './Actions'
import BrandFanInfluencerIdentityRowLabels from './Labels'
import BrandFanInfluencerIdentityRowPerformance from './Performance'
import BrandFanInfluencerIdentityRowTrackedRoute from './TrackedRoute'

interface IProps {
  brandFanMemberId?: number
  brandFanGroupId?: number
  identityId: number
  showCTAs?: boolean
  horizontalPadding?: number
  showLocationGenderAgeSection?: boolean
  showSubmissions?: boolean
}

const BrandFanInfluencerIdentityRow: React.FC<IProps> = ({
  identityId,
  brandFanMemberId,
  brandFanGroupId,
  horizontalPadding,
  showCTAs = true,
  showLocationGenderAgeSection = true,
  showSubmissions = true,
}) => {
  const dispatch = useDispatch()
  const isOpened = useSelector((state) => selectIsBrandFanIdentityOpened(state, identityId))
  const fullName = useSelector((state: IApplicationState) =>
    selectIdentityFullName(state, brandFanMemberId ?? identityId),
  )
  const age = useSelector((state: IApplicationState) =>
    selectIdentityAge(state, brandFanMemberId ?? identityId),
  )
  const locationAndGender = useSelector((state: IApplicationState) =>
    selectIdentityFormattedLocationAndGender(state, brandFanMemberId ?? identityId),
  )
  const socialUrl = useSelector((state: IApplicationState) =>
    selectIdentitySocialPageUrl(state, brandFanMemberId ?? identityId),
  )
  const avatarSrc = useSelector((state: IApplicationState) =>
    selectIdentityAvatar(state, brandFanMemberId ?? identityId),
  )
  const username = useSelector((state: IApplicationState) =>
    selectIdentityUsername(state, brandFanMemberId ?? identityId),
  )
  const provider = useSelector(
    (state: IApplicationState) =>
      selectIdentityProvider(state, brandFanMemberId ?? identityId) as SubmissionChannels,
  )
  const hasGroup = useSelector((state: IApplicationState) =>
    selectIdentityHasGroup(state, brandFanMemberId ?? identityId),
  )
  const hasIdentitySubmissionsLink = useSelector((state: IApplicationState) =>
    Boolean(selectIdentitySubmissionsLink(state, brandFanMemberId ?? identityId)),
  )

  const toggle = () => {
    dispatch(toggleIdentity(identityId))
  }

  const onFetch = () => {
    if (isOpened) {
      dispatch(fetchSubmissions(identityId))
    }
  }
  const viewSubmissionsAllowed = showSubmissions && hasIdentitySubmissionsLink

  useEffect(onFetch, [isOpened])

  return (
    <InfluencerIdentityRowRoot>
      <BrandFanInfluencerIdentityRowTrackedRoute
        identityId={identityId}
        eventName={EventTrackingNames.BrandFansRowClicked}
        additionalProperties={{
          action: isOpened ? 'close' : 'open',
        }}
      >
        <InfluencerIdentityRow
          onRowClick={viewSubmissionsAllowed ? toggle : undefined}
          horizontalPadding={horizontalPadding}
          avatarRightSpacing={1}
          fullName={fullName}
          socialUrl={socialUrl}
          avatarSrc={avatarSrc}
          username={username}
          provider={provider}
          showStar={hasGroup}
          age={age}
          locationAndGender={locationAndGender}
          showLocationGenderAge={showLocationGenderAgeSection}
          stats={
            <BrandFanInfluencerIdentityRowPerformance
              identityId={identityId}
              provider={provider}
              brandFanGroupId={brandFanGroupId}
              brandFanMemberId={brandFanMemberId}
            />
          }
          labels={
            showCTAs && (
              <BrandFanInfluencerIdentityRowLabels
                identityId={identityId}
                brandFanMemberId={brandFanMemberId}
                brandFanGroupId={brandFanGroupId}
              />
            )
          }
          actions={
            showCTAs && (
              <BrandFanInfluencerIdentityRowActions
                identityId={identityId}
                active={hasGroup}
                brandFanMemberId={brandFanMemberId}
                brandFanGroupId={brandFanGroupId}
              />
            )
          }
        />
      </BrandFanInfluencerIdentityRowTrackedRoute>
      {viewSubmissionsAllowed && isOpened && (
        <InfluencerIdentityRowSubmissionWrapper identityId={identityId} />
      )}
    </InfluencerIdentityRowRoot>
  )
}

export { BrandFanInfluencerIdentityRow }
export default React.memo(BrandFanInfluencerIdentityRow)
